import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  type BulkTablePasteMode,
  ensureBulkTableRows,
  getBulkTableClipboardColumnError,
  isBlankBulkTableRow,
  mergeBulkTableRows,
  parseTabularClipboard,
} from '../../../utils/bulk-table/clipboard';
import {
  getListPageExcelColumnFieldName,
  getListPageExcelColumnTitle,
} from '../../../utils/list-page-excel/columns';
import type { ListPageEditorFileResult } from '../../../utils/list-page-excel/editor-file';
import {
  buildListPageReplacementAnswers,
  getListPageEditorColumnHint,
  getListPageEditorColumnTotal,
  getListPageEditorColumnWidth,
  getListPageExcelSchema,
  isListPageEditorColumnRequired,
  type ListPageExcelOptions,
  validateListPageEditorRows,
} from '../../../utils/list-page-excel/editor-model';
import { downloadListPageWorkbook } from '../../../utils/list-page-excel/workbook';
import { mapPsfaCdrClipboard } from '../../../utils/list-page-excel/psfa-cdr-clipboard';
import {
  getListQuestionAvailableOptions,
  isListQuestionActive,
} from '../../../utils/list-question-utils';
import { parseSafeHtml } from '../../../utils/safe-html';
import BulkTableModal from './BulkTableModal';
import InputMethodButton from './InputMethodButton';
import { HiddenExcelFileInput } from './ExcelToolbar';

const MINIMUM_ROWS = 5;

const Intro = styled.p`
  max-width: 70ch;
`;

const TableViewport = styled.div`
  max-height: 56vh;
  overflow: auto;
  border: 1px solid #b1b4b6;
  box-shadow: inset 0 -8px 12px -12px rgba(11, 12, 12, 0.5);
`;

const EditorTable = styled.table`
  min-width: 100%;
  margin: 0;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  th,
  td {
    padding: 10px;
    border-right: 1px solid #b1b4b6;
    border-bottom: 1px solid #b1b4b6;
    vertical-align: top;
  }

  th:last-child,
  td:last-child {
    border-right: 0;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 3;
  }

  thead th {
    background: #d9eaf7;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

const Requirement = styled.span`
  display: block;
  margin-top: 6px;
  color: #505a5f;
  font-size: 0.875rem;
  font-weight: 400;
`;

const RequiredMarker = styled.span`
  margin-left: 3px;
  color: #d4351c;
`;

const DetailsButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-left: 7px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #ffffff;
  background: #1d70b8;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
  cursor: pointer;

  &:hover {
    background: #003078;
  }

  &:focus {
    outline: 3px solid #ffdd00;
    outline-offset: 1px;
    color: #0b0c0c;
    background: #ffdd00;
  }
`;

const DetailsContent = styled.div`
  max-width: 70ch;
`;

const GuidanceCell = styled.th`
  padding: 8px 10px;
  color: #0b0c0c;
  background: #f3f2f1 !important;
  font-weight: 400;
  text-align: left;

  strong {
    margin-right: 10px;
  }
`;

const EditorCell = styled.td<{ $invalid: boolean; $newRow: boolean }>`
  background: ${({ $invalid, $newRow }) =>
    $invalid ? '#f6d7d2' : $newRow ? '#fff7bf' : '#ffffff'};

  .govuk-input,
  .govuk-select {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .govuk-input--error,
  .govuk-select--error {
    border: 4px solid #d4351c;
  }
`;

const TotalsCell = styled.td<{ $hasTotal: boolean }>`
  position: sticky;
  z-index: 2;
  bottom: 0;
  padding: 8px 10px 10px;
  border-top: 3px solid #1d70b8;
  background: ${({ $hasTotal }) => ($hasTotal ? '#eaf4f9' : '#f3f2f1')};
  text-align: ${({ $hasTotal }) => ($hasTotal ? 'right' : 'left')};
  vertical-align: middle;
  box-shadow: 0 -8px 14px -14px rgba(11, 12, 12, 0.7);
`;

const TotalLabel = styled.span`
  display: block;
  color: #505a5f;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const TotalValue = styled.strong`
  display: block;
  margin-top: 2px;
  color: #003078;
  font-size: 1.125rem;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
`;

const PasteChoice = styled.div`
  margin: 20px 0;
  padding: 20px;
  border-left: 5px solid #1d70b8;
  background: #f3f2f1;

  .govuk-heading-m {
    margin-bottom: 15px;
  }
`;

const FocusedError = styled.div`
  position: fixed;
  z-index: 1010;
  bottom: 20px;
  left: 50%;
  width: min(calc(100% - 40px), 720px);
  padding: 15px 20px;
  transform: translateX(-50%);
  border: 4px solid #d4351c;
  color: #0b0c0c;
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(11, 12, 12, 0.3);
`;

type Props = {
  options: ListPageExcelOptions;
  initialRows: unknown[][];
  initialMessage?: string;
  disabled?: boolean;
  onClose: () => void;
  onChangeInputMethod?: () => void;
  onSave: (answers: Record<string, unknown>) => void | Promise<void>;
  onSaveUnfinished?: (answers: Record<string, unknown>) => void | Promise<void>;
  readFile: (file: File) => Promise<ListPageEditorFileResult>;
};

export default function ListPageExcelEditorModal({
  options,
  initialRows,
  initialMessage,
  disabled = false,
  onClose,
  onChangeInputMethod,
  onSave,
  onSaveUnfinished,
  readFile,
}: Props) {
  const schema = useMemo(() => getListPageExcelSchema(options.questions), [options.questions]);
  const columnWidths = useMemo(
    () => schema.columns.map(getListPageEditorColumnWidth),
    [schema.columns],
  );
  const tableWidth = columnWidths.reduce((total, width) => total + width, 0);
  const [rows, setRows] = useState(() =>
    ensureBulkTableRows(initialRows, schema.columns.length, MINIMUM_ROWS),
  );
  const [message, setMessage] = useState(initialMessage ?? '');
  const [focusedCell, setFocusedCell] = useState('');
  const [pendingPasteRows, setPendingPasteRows] = useState<string[][]>();
  const [pasteMessage, setPasteMessage] = useState('');
  const [showEmptyError, setShowEmptyError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [details, setDetails] = useState<{ heading: string; content?: string }>();
  const [newRowIndexes, setNewRowIndexes] = useState(() => new Set<number>());
  const [pendingFocusRowIndex, setPendingFocusRowIndex] = useState<number>();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const cellRefs = useRef(new Map<string, HTMLInputElement | HTMLSelectElement>());

  const validation = useMemo(
    () => validateListPageEditorRows(options, rows, schema),
    [options, rows, schema],
  );
  const errorsByCell = useMemo(() => {
    const next = new Map<string, string[]>();
    validation.errors.forEach((error) => {
      const key = `${error.worksheetRow}:${error.columnIndex}`;
      const messages = next.get(key) ?? [];
      if (!messages.includes(error.message)) messages.push(error.message);
      next.set(key, messages);
    });
    if (showEmptyError && validation.rows.length === 0) {
      next.set('1:1', ['Enter at least one response before saving.']);
    }
    return next;
  }, [showEmptyError, validation.errors, validation.rows.length]);
  const answersByRow = useMemo(
    () => new Map(validation.rows.map((row) => [row.worksheetRow, row.answers])),
    [validation.rows],
  );
  const focusedError = errorsByCell.get(focusedCell)?.join(' ');
  const defaultGuidance = schema.columns
    .flatMap(({ descriptors }) => descriptors)
    .map(({ question }) => String(question.hint ?? '').trim())
    .find(Boolean);
  const displayedGuidance = (() => {
    if (!focusedCell) return defaultGuidance;
    const [rowNumber, columnNumber] = focusedCell.split(':').map(Number);
    const column = schema.columns[columnNumber - 1];
    if (!column) return undefined;
    const rowAnswers = answersByRow.get(rowNumber) ?? {};
    return column.descriptors
      .filter((descriptor) => isListQuestionActive(descriptor, rowAnswers))
      .map(({ question }) => String(question.hint ?? '').trim())
      .find(Boolean);
  })();

  const getColumnDetails = (columnIndex: number) => {
    const column = schema.columns[columnIndex];
    if (!column) return undefined;
    const [rowNumber, focusedColumnNumber] = focusedCell.split(':').map(Number);
    const rowAnswers =
      focusedColumnNumber === columnIndex + 1 ? (answersByRow.get(rowNumber) ?? {}) : {};
    const descriptor =
      column.descriptors.find(
        (candidate) =>
          candidate.kind === 'list' &&
          isListQuestionActive(candidate, rowAnswers) &&
          candidate.question.detailsHeading,
      ) ??
      column.descriptors.find(({ kind, question }) => kind === 'list' && question.detailsHeading);
    if (!descriptor?.question.detailsHeading) return undefined;
    return {
      heading: descriptor.question.detailsHeading,
      content: descriptor.question.detailsContent,
    };
  };

  useEffect(() => {
    if (pendingFocusRowIndex === undefined) return;
    const element = cellRefs.current.get(`${pendingFocusRowIndex + 1}:1`);
    if (!element) return;
    element.focus({ preventScroll: true });
    element.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    setPendingFocusRowIndex(undefined);
  }, [pendingFocusRowIndex, rows.length]);

  const setCellValue = (rowIndex: number, columnIndex: number, value: string) => {
    setRows((currentRows) =>
      currentRows.map((row, currentRowIndex) =>
        currentRowIndex === rowIndex
          ? row.map((cell, currentColumnIndex) =>
              currentColumnIndex === columnIndex ? value : cell,
            )
          : row,
      ),
    );
    if (String(value).trim()) {
      setNewRowIndexes((currentIndexes) => {
        if (!currentIndexes.has(rowIndex)) return currentIndexes;
        const nextIndexes = new Set(currentIndexes);
        nextIndexes.delete(rowIndex);
        return nextIndexes;
      });
    }
    setShowEmptyError(false);
    setMessage('');
  };

  const handleAddRow = () => {
    const newRowIndex = rows.length;
    setRows([...rows, Array.from({ length: schema.columns.length }, () => '')]);
    setNewRowIndexes((currentIndexes) => new Set(currentIndexes).add(newRowIndex));
    setPendingFocusRowIndex(newRowIndex);
    setMessage('');
  };

  const handlePasteFromClipboard = async () => {
    setMessage('');
    try {
      const text = await navigator.clipboard?.readText?.();
      const parsedRows = text ? parseTabularClipboard(text) : [];
      const mapped =
        options.tenant === 'psfa'
          ? mapPsfaCdrClipboard(parsedRows, options.pageName, schema.columns)
          : { rows: parsedRows };
      if (mapped.error) {
        setPendingPasteRows(undefined);
        setMessage(mapped.error);
        return;
      }
      const pastedRows = mapped.rows;
      setPasteMessage(mapped.message ?? '');
      if (pastedRows.length === 0) {
        setMessage('Clipboard does not contain any rows to paste.');
        return;
      }
      const columnError = getBulkTableClipboardColumnError(pastedRows, schema.columns.length);
      if (columnError) {
        setPendingPasteRows(undefined);
        setMessage(columnError);
        return;
      }
      setPendingPasteRows(pastedRows);
    } catch (_error) {
      setMessage('Clipboard could not be read. Allow clipboard access and try again.');
    }
  };

  const applyPaste = (mode: BulkTablePasteMode) => {
    if (!pendingPasteRows) return;
    const pastedRows = pendingPasteRows.map((row) =>
      Array.from({ length: schema.columns.length }, (_unused, columnIndex) =>
        String(row[columnIndex] ?? '').trim(),
      ),
    );
    const mergedRows = mergeBulkTableRows(rows, pastedRows, mode);
    setRows(ensureBulkTableRows(mergedRows, schema.columns.length, MINIMUM_ROWS));
    setNewRowIndexes(new Set());
    setPendingPasteRows(undefined);
    setShowEmptyError(false);
    setMessage(
      pasteMessage ||
        (mode === 'replace'
          ? 'Replaced current rows with clipboard data.'
          : 'Added clipboard data below current rows.'),
    );
  };

  const handleUpload = async (file?: File) => {
    if (!file) return;
    setIsLoadingFile(true);
    setMessage('');
    try {
      const result = await readFile(file);
      if (result.status === 'invalid-template') {
        setMessage('The template you uploaded is out of date or has been modified.');
        return;
      }
      setRows(ensureBulkTableRows(result.rows, schema.columns.length, MINIMUM_ROWS));
      setNewRowIndexes(new Set());
      setShowEmptyError(false);
      setMessage(result.message ?? 'Loaded Excel rows. Review them before saving.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The XLSX file could not be imported.');
    } finally {
      setIsLoadingFile(false);
    }
  };

  const handleDownload = async () => {
    setMessage('');
    try {
      await downloadListPageWorkbook(
        options,
        rows.filter((row) => !isBlankBulkTableRow(row)),
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The XLSX file could not be created.');
    }
  };

  const handleSave = async (saveUnfinished = false) => {
    setShowEmptyError(!saveUnfinished);
    if (!saveUnfinished && (validation.rows.length === 0 || validation.errors.length > 0)) {
      setMessage(
        validation.rows.length === 0
          ? 'Enter at least one response before saving.'
          : `${validation.errors.length} validation error${validation.errors.length === 1 ? '' : 's'} found. Fix the red cells before saving.`,
      );
      const firstError =
        validation.errors.length > 0
          ? `${validation.errors[0].worksheetRow}:${validation.errors[0].columnIndex}`
          : '1:1';
      setFocusedCell(firstError);
      cellRefs.current.get(firstError)?.focus();
      return;
    }

    setIsSaving(true);
    setMessage('');
    try {
      await (saveUnfinished && onSaveUnfinished ? onSaveUnfinished : onSave)(
        buildListPageReplacementAnswers(options, validation.rows),
      );
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The spreadsheet could not be saved.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BulkTableModal title={`Edit ${options.title}`} onClose={onClose} fullScreen>
      {onChangeInputMethod && (
        <div className="govuk-button-group govuk-!-margin-bottom-4">
          <InputMethodButton onClick={onChangeInputMethod} />
        </div>
      )}
      <Intro className="govuk-body">
        Enter rows in this table, paste rows from a spreadsheet, or upload an Excel file.
      </Intro>
      {message && (
        <p className="govuk-error-message" role="alert">
          <span className="govuk-visually-hidden">Status:</span> {message}
        </p>
      )}
      <TableViewport>
        <EditorTable className="govuk-table" style={{ width: `max(100%, ${tableWidth}ch)` }}>
          <colgroup>
            {schema.columns.map((column, columnIndex) => (
              <col
                key={getListPageExcelColumnFieldName(column)}
                style={{ width: `${columnWidths[columnIndex]}ch` }}
              />
            ))}
          </colgroup>
          <thead className="govuk-table__head">
            <tr className="govuk-table__row">
              {schema.columns.map((column, columnIndex) => {
                const columnDetails = getColumnDetails(columnIndex);
                return (
                  <th
                    key={getListPageExcelColumnFieldName(column)}
                    scope="col"
                    className="govuk-table__header"
                  >
                    {getListPageExcelColumnTitle(column)}
                    {isListPageEditorColumnRequired(column) && (
                      <RequiredMarker aria-label="required">*</RequiredMarker>
                    )}
                    {columnDetails && (
                      <DetailsButton
                        type="button"
                        aria-label={`Show ${columnDetails.heading}`}
                        onClick={() => setDetails(columnDetails)}
                      >
                        <span aria-hidden="true">i</span>
                      </DetailsButton>
                    )}
                    {getListPageEditorColumnHint(column) && (
                      <Requirement>{getListPageEditorColumnHint(column)}</Requirement>
                    )}
                  </th>
                );
              })}
            </tr>
            {displayedGuidance && (
              <tr className="govuk-table__row">
                <GuidanceCell colSpan={schema.columns.length} scope="colgroup">
                  <strong>Guidance</strong>
                  {displayedGuidance}
                </GuidanceCell>
              </tr>
            )}
          </thead>
          <tbody className="govuk-table__body">
            {rows.map((row, rowIndex) => {
              const rowAnswers = answersByRow.get(rowIndex + 1) ?? {};
              return (
                <tr className="govuk-table__row" key={`editor-row-${rowIndex + 1}`}>
                  {schema.columns.map((column, columnIndex) => {
                    const cellKey = `${rowIndex + 1}:${columnIndex + 1}`;
                    const error = errorsByCell.get(cellKey);
                    const activeDescriptors = column.descriptors.filter((descriptor) =>
                      isListQuestionActive(descriptor, rowAnswers),
                    );
                    const activeDescriptor =
                      activeDescriptors.length === 1 ? activeDescriptors[0] : undefined;
                    const descriptor = activeDescriptor ?? column.descriptors[0];
                    const value = String(row[columnIndex] ?? '');
                    const availableOptions = activeDescriptor
                      ? getListQuestionAvailableOptions(activeDescriptor, rowAnswers)
                      : [];
                    const label = `${getListPageExcelColumnTitle(column)}, row ${rowIndex + 1}`;
                    const commonProps = {
                      'aria-invalid': error ? ('true' as const) : undefined,
                      'aria-label': label,
                      className: error ? 'govuk-input govuk-input--error' : 'govuk-input',
                      disabled,
                      onFocus: () => setFocusedCell(cellKey),
                      ref: (element: HTMLInputElement | HTMLSelectElement | null) => {
                        if (element) cellRefs.current.set(cellKey, element);
                        else cellRefs.current.delete(cellKey);
                      },
                    };
                    return (
                      <EditorCell
                        key={cellKey}
                        $invalid={Boolean(error)}
                        $newRow={newRowIndexes.has(rowIndex)}
                      >
                        {descriptor.kind === 'list' && activeDescriptor ? (
                          <select
                            {...commonProps}
                            className={error ? 'govuk-select govuk-select--error' : 'govuk-select'}
                            value={value}
                            onChange={(event) =>
                              setCellValue(rowIndex, columnIndex, event.target.value)
                            }
                          >
                            <option value="">Select</option>
                            {value &&
                              !availableOptions.some(
                                (option) => option.label === value || option.value === value,
                              ) && (
                                <option value={value} disabled>
                                  {value}
                                </option>
                              )}
                            {availableOptions.map((option) => (
                              <option key={option.value} value={option.label}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            {...commonProps}
                            type="text"
                            inputMode={
                              descriptor.kind === 'number'
                                ? descriptor.question.rules?.decimal ||
                                  descriptor.question.rules?.float
                                  ? 'decimal'
                                  : 'numeric'
                                : undefined
                            }
                            value={value}
                            onChange={(event) =>
                              setCellValue(rowIndex, columnIndex, event.target.value)
                            }
                          />
                        )}
                      </EditorCell>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              {schema.columns.map((column, columnIndex) => {
                const title = getListPageExcelColumnTitle(column);
                const total = getListPageEditorColumnTotal(column, rows, columnIndex);
                return (
                  <TotalsCell
                    key={getListPageExcelColumnFieldName(column)}
                    $hasTotal={total !== undefined}
                    aria-label={
                      total === undefined ? `No total for ${title}` : `Total for ${title}`
                    }
                  >
                    {total !== undefined && (
                      <>
                        <TotalLabel aria-hidden="true">Total</TotalLabel>
                        <TotalValue>{total}</TotalValue>
                      </>
                    )}
                  </TotalsCell>
                );
              })}
            </tr>
          </tfoot>
        </EditorTable>
      </TableViewport>
      <div className="govuk-button-group govuk-!-margin-top-4">
        <button
          className="govuk-button govuk-button--secondary"
          type="button"
          disabled={disabled}
          onClick={handleAddRow}
        >
          Add row
        </button>
        <button
          className="govuk-button govuk-button--secondary"
          type="button"
          disabled={disabled}
          onClick={() => void handlePasteFromClipboard()}
        >
          Paste from clipboard
        </button>
        <button
          className="govuk-button govuk-button--secondary"
          type="button"
          onClick={() => void handleDownload()}
        >
          Download Excel template
        </button>
        <HiddenExcelFileInput
          ref={uploadInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          tabIndex={-1}
          onChange={(event) => {
            void handleUpload(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        <button
          className="govuk-button govuk-button--secondary"
          type="button"
          disabled={disabled || isLoadingFile}
          onClick={() => uploadInputRef.current?.click()}
        >
          Upload Excel template
        </button>
      </div>
      {pendingPasteRows && (
        <PasteChoice>
          <h3 className="govuk-heading-m">How should we add the copied rows?</h3>
          <div className="govuk-button-group">
            <button className="govuk-button" type="button" onClick={() => applyPaste('replace')}>
              Replace current rows
            </button>
            <button
              className="govuk-button govuk-button--secondary"
              type="button"
              onClick={() => applyPaste('append')}
            >
              Add below current rows
            </button>
            <button
              className="govuk-button govuk-button--secondary"
              type="button"
              onClick={() => setPendingPasteRows(undefined)}
            >
              Cancel
            </button>
          </div>
        </PasteChoice>
      )}
      <div className="govuk-button-group govuk-!-margin-top-5">
        <button
          className="govuk-button"
          type="button"
          disabled={disabled || isSaving}
          onClick={() => void handleSave()}
        >
          Save changes
        </button>
        {onSaveUnfinished && (
          <button
            className="govuk-button govuk-button--secondary"
            type="button"
            disabled={disabled || isSaving}
            onClick={() => void handleSave(true)}
          >
            Save as unfinished
          </button>
        )}
        <button className="govuk-button govuk-button--secondary" type="button" onClick={onClose}>
          Cancel
        </button>
      </div>
      {focusedError && (
        <FocusedError role="status" aria-live="polite">
          {focusedError}
        </FocusedError>
      )}
      {details && (
        <BulkTableModal
          title={details.heading}
          onClose={() => setDetails(undefined)}
          width="min(100%, 720px)"
          zIndex={1020}
        >
          <DetailsContent className="govuk-body">{parseSafeHtml(details.content)}</DetailsContent>
          <button className="govuk-button" type="button" onClick={() => setDetails(undefined)}>
            Close
          </button>
        </BulkTableModal>
      )}
    </BulkTableModal>
  );
}
